// @rpg-unit/interact-prompt: the interact prompt (tier 4; composition framework, decision 0077). Cut from spike 002's
// `src/things/prompt.ts` (the prompt over the chosen thing, gate C5, its decision 0044) and the interaction step in
// `src/things/module.ts` on 2026-10-20. Over @rpg/controller's chooser (`Interaction`: the nearest valid thing by
// reach, facing, cone and priority) it gives a game:
//
//   - `interactStep(o)`: one sim step of choosing and firing: the chooser picks from the actor's feet and facing, an
//     interact press fires the chosen thing, and the standard `interact:request` event goes out (@rpg/contracts) with
//     the thing's id and kind; `interactDone(o)` sends `interact:done` once the thing has acted (a door opened, a
//     conversation begun, a node gathered). Doors, shops, dialogue and gathering listen; none is imported;
//   - `createInteractPrompt(o)`: the device-aware prompt drawn over the chosen thing ("[F] Read"): the `interact`
//     action's glyph for the device used last (a key, a pad button, a tap), the verb through the strings, placed where
//     the game projects the thing each frame; on touch the overlay's interact button shows while something is in reach.
//     The game calls `update()` from its own frame callback, so it keeps its own order.
//
// The seams: the standard `interact` action and the two standard events; the chooser's state stays the game's (it is
// saved with the game's sim); the projection, the glyphs and the strings are ports.
import type { Interaction, InteractChoice, InteractionState } from "@rpg/controller";
import type { StandardEvents } from "@rpg/contracts";

/** This unit's version (the manifest's and package.json's). */
export const INTERACT_PROMPT_VERSION = "0.1.0" as const;

/** The standard events this feature emits. */
export type InteractEvents = Pick<StandardEvents, "interact:request" | "interact:done">;
/** Where events go (a kernel event bus fits). */
export type InteractEmit = <K extends keyof InteractEvents>(name: K, payload: InteractEvents[K]) => void;

/** Planted defects, so the checks can prove they fail: `noRequestEvent` fires without telling anyone; `staleCap`
 *  keeps the first device's glyph after the player switches device. */
export const INTERACT_PROMPT_FAULTS = { noRequestEvent: false, staleCap: false };

/** What one step needs. */
export interface InteractStepOptions {
  interaction: Pick<Interaction, "update" | "fire">;
  /** The chooser's state (the game's sim keeps and saves it). */
  state: InteractionState;
  /** The actor's feet and facing. */
  at: { pos: { x: number; y: number; z: number }; yaw: number };
  /** Was interact pressed this step? */
  pressed: boolean;
  /** Who acts (an entity id), or null. */
  actor: number | null;
  /** A thing's kind for the events (`door`, `npc`, `gatherable`, …). */
  kindOf: (id: string) => string;
  /** Where the events go (none: no events). */
  emit?: InteractEmit | null;
}

/** One step's result: the choice, what fired (or null), and whether the press went to a thing. */
export interface InteractStep {
  choice: InteractChoice;
  fired: { id: string; verb: string } | null;
  claimed: boolean;
}

/** Choose and fire for one step, and send `interact:request` for what fired. */
export function interactStep(o: InteractStepOptions): InteractStep {
  const choice = o.interaction.update(o.at, o.state);
  const claimed = choice.id !== null && o.pressed;
  const fired = o.interaction.fire(o.pressed, o.state);
  if (fired && !INTERACT_PROMPT_FAULTS.noRequestEvent) o.emit?.("interact:request", { actor: o.actor, target: fired.id, kind: o.kindOf(fired.id) });
  return { choice, fired: fired ? { id: fired.id, verb: fired.verb } : null, claimed };
}

/** Send `interact:done`: the thing acted. */
export function interactDone(o: { emit?: InteractEmit | null; actor: number | null; target: string; kind: string }): void {
  o.emit?.("interact:done", { actor: o.actor, target: o.target, kind: o.kind });
}

/** A device family as @rpg/runtime names it. */
export type DeviceFamily = "keyboardMouse" | "gamepad" | "touch";

/** What the prompt shows (for a test handle). */
export interface PromptState {
  shown: boolean;
  id: string | null;
  device: DeviceFamily | null;
  family: DeviceFamily | null;
  cap: string;
  verb: string;
  /** Screen position (px) of the prompt's anchor. */
  x: number;
  y: number;
  touchButton: boolean;
}

/** The prompt's style (the game can restyle `.rpg-interact`). */
export const INTERACT_PROMPT_CSS = `
.rpg-interact{position:fixed;left:0;top:0;z-index:12;display:none;align-items:center;gap:6px;padding:4px 9px 4px 5px;border-radius:999px;
  background:rgba(20,28,24,.72);color:#f4efe2;font:600 14px/1.1 system-ui,sans-serif;pointer-events:none;white-space:nowrap;transform:translate(-50%,-100%)}
.rpg-interact[data-shown]{display:flex}
.rpg-interact kbd{font:700 12px/1 system-ui,sans-serif;padding:4px 7px;border-radius:6px;background:#f4efe2;color:#1d2a1f;min-width:12px;text-align:center}
.rpg-interact[data-device=gamepad] kbd{border-radius:999px;background:#e6c35a}
.rpg-interact[data-device=touch] kbd{background:transparent;color:#f4efe2;border:1px solid #f4efe2}
`;

/** What the prompt needs from the game. */
export interface InteractPromptOptions {
  /** The input: the `interact` action's glyph for the device used last (@rpg/runtime's `Input` fits), and the device. */
  input: {
    glyph(action: string, glyphs: never): { glyph: string; family: DeviceFamily } | null;
    controls: { device: DeviceFamily | null };
  };
  /** The game's glyph table (family → glyph key → string id). */
  glyphs: unknown;
  t: (id: string) => string;
  /** What to prompt for now: the chosen thing and its verb's string id (id null: nothing, or the actor is busy). */
  choice: () => { id: string | null; verb: string | null };
  /** Where a thing's prompt sits on screen, px (the game projects its anchor through its camera). */
  screenAt: (id: string) => { x: number; y: number };
  /** The touch overlay's interact button (shown while something is in reach). */
  overlay?: { reveal(button: string, on: boolean): void } | null;
  /** The action's name (default `interact`, the standard one). */
  action?: string;
  /** The document (default `document`); the prompt is appended to its body. */
  doc?: Document;
}

/** Make the prompt (hidden until something is chosen); `update()` it each frame after the camera moves. */
export function createInteractPrompt(o: InteractPromptOptions): { element: HTMLElement; update(): void; state(): PromptState } {
  const doc = o.doc ?? document;
  const action = o.action ?? "interact";
  if (!doc.getElementById("rpg-interact-css")) {
    const s = doc.createElement("style");
    s.id = "rpg-interact-css";
    s.textContent = INTERACT_PROMPT_CSS;
    doc.head.appendChild(s);
  }
  const el = doc.createElement("div");
  el.className = "rpg-interact";
  el.dataset.prompt = "interact";
  el.setAttribute("role", "status");
  const kbd = doc.createElement("kbd");
  const verb = doc.createElement("span");
  el.append(kbd, verb);
  doc.body.appendChild(el);
  let st: PromptState = { shown: false, id: null, device: null, family: null, cap: "", verb: "", x: 0, y: 0, touchButton: false };
  let touchShown = false;
  let firstCap: { cap: string; family: DeviceFamily } | null = null;

  const update = (): void => {
    const ch = o.choice();
    const id = ch.id;
    const g = o.input.glyph(action, o.glyphs as never);
    let cap = g ? o.t(g.glyph) : "";
    let family = g?.family ?? null;
    if (INTERACT_PROMPT_FAULTS.staleCap) {
      firstCap ??= g ? { cap, family: g.family } : null;
      if (firstCap) ({ cap, family } = firstCap);
    }
    const verbText = id && ch.verb ? o.t(ch.verb) : "";
    let x = 0;
    let y = 0;
    if (id) {
      ({ x, y } = o.screenAt(id));
      el.style.left = `${x.toFixed(1)}px`;
      el.style.top = `${y.toFixed(1)}px`;
      if (kbd.textContent !== cap) kbd.textContent = cap;
      if (verb.textContent !== verbText) verb.textContent = verbText;
      el.dataset.device = family ?? "keyboardMouse";
      el.dataset.shown = "";
    } else el.removeAttribute("data-shown");
    // the touch overlay's interact button, while something is in reach
    if (touchShown !== (id !== null)) {
      touchShown = id !== null;
      o.overlay?.reveal(action, touchShown);
    }
    st = { shown: id !== null, id, device: o.input.controls.device, family, cap, verb: verbText, x, y, touchButton: touchShown };
  };
  return { element: el, update, state: () => ({ ...st }) };
}
